<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const truncate = (text, length = 150) => {
  if (!text) return '-';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const handleViewQuestions = document => {
  emit('action', { action: 'viewRelatedQuestions', id: document.id });
};

const handleDelete = document => {
  emit('action', { action: 'delete', id: document.id });
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
  >
    <table class="w-full">
      <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
        <tr>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-file-text size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.DOCUMENTS.TABLE.TITLE') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-text size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.DOCUMENTS.TABLE.DESCRIPTION') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-calendar size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.DOCUMENTS.TABLE.CREATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-clock size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.DOCUMENTS.TABLE.UPDATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11"
          >
            <div class="flex items-center justify-end gap-1.5">
              <span>{{ t('CAPTAIN.DOCUMENTS.TABLE.ACTIONS') }}</span>
              <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-n-solid-1">
        <tr
          v-for="document in documents"
          :key="document.id"
          class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
        >
          <td class="px-5 py-3">
            <span class="text-sm font-medium text-n-slate-12 block">
              {{
                document.name || document.external_link || document.title || '-'
              }}
            </span>
          </td>
          <td class="px-5 py-3">
            <span class="text-sm text-n-slate-10 line-clamp-2">
              {{ truncate(document.content || document.external_link, 150) }}
            </span>
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatDate(document.createdAt || document.created_at) }}
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{
              formatDate(
                document.updatedAt ||
                  document.updated_at ||
                  document.createdAt ||
                  document.created_at
              )
            }}
          </td>
          <td class="px-5 py-3">
            <div class="flex justify-end gap-2">
              <Button
                icon="i-lucide-message-circle-question"
                variant="ghost"
                size="sm"
                color="purple"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.DOCUMENTS.TABLE.VIEW_QUESTIONS')"
                @click="handleViewQuestions(document)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.DOCUMENTS.TABLE.DELETE')"
                @click="handleDelete(document)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
